if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]:not([hidden])`) ||
          this.elements.viewer.querySelector('[data-media-id]:not([hidden])') ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      getMediaIdFromGalleryKey(key) {
        return String(key).split('-').pop();
      }

      setMediaVisibility(mediaId, isVisible) {
        const mediaItem = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
        if (!mediaItem) return;

        mediaItem.hidden = !isVisible;
        mediaItem.style.display = isVisible ? '' : 'none';

        if (this.elements.thumbnails) {
          const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (thumbnail) {
            thumbnail.hidden = !isVisible;
            thumbnail.style.display = isVisible ? '' : 'none';
          }
        }

        if (!isVisible && mediaItem.classList.contains('is-active')) {
          const nextMedia = this.elements.viewer.querySelector('[data-media-id]:not([hidden])');
          if (nextMedia) {
            this.setActiveMedia(nextMedia.dataset.mediaId, false);
          }
        }
      }

      setMediaVisibilityBySelector(selector, isVisible) {
        this.elements.viewer.querySelectorAll(selector).forEach((item) => {
          this.setMediaVisibility(item.dataset.mediaId, isVisible);
        });
      }

      applyVariantMediaSegment(featuredMediaId, mediaOrder = [], variantFeatureIds = []) {
        if (!featuredMediaId) return;

        const featuredKey = this.getMediaIdFromGalleryKey(featuredMediaId);
        const featureKeys = new Set(variantFeatureIds.map((id) => String(id)));

        // Fallback: if mapping data is unavailable, keep all media visible.
        if (mediaOrder.length === 0 || featureKeys.size === 0) {
          this.setMediaVisibilityBySelector('[data-media-id]', true);
          return;
        }

        const segments = [];
        let currentSegment = null;

        mediaOrder.forEach((mediaId) => {
          const id = String(mediaId);
          if (featureKeys.has(id)) {
            if (currentSegment) segments.push(currentSegment);
            currentSegment = [id];
          } else if (currentSegment) {
            currentSegment.push(id);
          }
        });
        if (currentSegment) segments.push(currentSegment);

        const currentMediaGroup = segments.find((segment) => segment[0] === featuredKey);

        // Fallback: if current featured media is not part of computed segments, do not hide everything.
        if (!currentMediaGroup) {
          this.setMediaVisibilityBySelector('[data-media-id]', true);
          return;
        }

        const visibleKeys = new Set(currentMediaGroup || []);

        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((item) => {
          const itemKey = this.getMediaIdFromGalleryKey(item.dataset.mediaId);
          this.setMediaVisibility(item.dataset.mediaId, visibleKeys.has(itemKey));
        });

        if (this.elements.viewer.slider) {
          this.elements.viewer.resetPages();
        }
        if (this.elements.thumbnails?.slider) {
          this.elements.thumbnails.resetPages();
        }

        const firstVisibleMedia = this.elements.viewer.querySelector('[data-media-id]:not([hidden])');
        if (firstVisibleMedia) {
          this.setActiveMedia(firstVisibleMedia.dataset.mediaId, false);
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
